import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Welcome aboard");
      navigate("/map");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col px-6 py-10 md:px-16 bg-white">
        <Link to="/" data-testid="register-logo-link" className="flex items-center gap-2.5 mb-16">
          <img
            src="/brand/ctc-logo-240.png"
            alt="Coast To Coast Permits"
            className="h-14 w-auto object-contain"
            width="45"
            height="56"
          />
          <span className="font-outfit font-bold text-slate-900 text-base">Coast To Coast Permitting</span>
        </Link>

        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">Get started</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-slate-900 mb-3">Create your account</h1>
          <p className="text-slate-600 mb-10">Free for personal & small team use. No credit card.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                placeholder="Jane Architect"
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                placeholder="you@company.com"
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password-input"
                placeholder="At least 6 characters"
                className="mt-2 h-11"
              />
            </div>
            {error && (
              <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {loading ? "Creating…" : "Create account"}
              {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
          </form>

          <div className="mt-8 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-to-login" className="font-medium text-slate-900 hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/34753/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Map detail"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/80 via-slate-900/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-16 text-white">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-200 mb-3">Free forever tier</div>
          <h2 className="font-outfit text-4xl font-semibold tracking-tight max-w-md">
            One map. 3,221 counties. Zero spreadsheets.
          </h2>
        </div>
      </div>
    </div>
  );
}
